import {
  useLayoutEffect,
  RefObject,
  useCallback,
  useEffect,
  useMemo
} from 'react';
import {
  useFloating,
  Placement as FloatingUIPlacement,
  Middleware,
  flip,
  limitShift,
  shift,
  autoUpdate
} from '@floating-ui/react';

export type Placement = FloatingUIPlacement;
export type Modifiers = Middleware[];

export type ReferenceProp =
  | ReferenceObject
  | HTMLElement
  | RefObject<HTMLElement>;

export interface ReferenceObject {
  /** The top offset of the reference, in pixels. */
  top: number;
  /** The left offset of the reference, in pixels. */
  left: number;
  /** The height of the reference, in pixels. */
  height: number;
  /** The width of the reference, in pixels. */
  width: number;
}

export interface PositionOptions {
  /**
   * Element or virtual reference object to position against.
   */
  reference?: Element | ReferenceObject;
  /**
   * The floating element being positioned relative to the reference.
   */
  floating?: HTMLElement;
  /**
   * Preferred placement of the floating element relative to the reference.
   * @default 'top'
   */
  placement?: Placement;
  /**
   * Floating UI middleware applied when computing the position.
   * @default [flip(), shift({ limiter: limitShift() })]
   */
  modifiers?: Modifiers;
  /**
   * Track the cursor position as the reference instead of an element.
   */
  followCursor?: boolean;
}
/**
 * Hook for positioning an element relative to another.
 */
export const usePosition = ({
  reference,
  floating,
  followCursor,
  placement = 'top',
  modifiers = [flip(), shift({ limiter: limitShift() })]
}: PositionOptions = {}) => {
  const isVirtualElement = useMemo(
    () => !(reference as Element)?.nodeType,
    [reference]
  );

  const { refs, floatingStyles, update } = useFloating({
    open: true,
    placement,
    middleware: modifiers,
    elements: {
      reference: isVirtualElement ? null : (reference as Element),
      floating: floating
    },
    whileElementsMounted: autoUpdate
  });

  useEffect(() => {
    if (isVirtualElement && reference && !followCursor) {
      const refObject = reference as ReferenceObject;
      refs.setPositionReference({
        getBoundingClientRect() {
          return {
            width: refObject.width,
            height: refObject.height,
            x: refObject.left,
            y: refObject.top,
            left: refObject.left,
            top: refObject.top,
            right: refObject.left + refObject.width,
            bottom: refObject.top + refObject.height
          };
        }
      });
    }
  }, [reference, refs, isVirtualElement, followCursor]);

  const onMouseMove = useCallback(
    ({ clientX, clientY }: MouseEvent) => {
      // Virtual reference object for cursor position.
      refs.setPositionReference({
        getBoundingClientRect() {
          return {
            width: 0,
            height: 0,
            x: clientX,
            y: clientY,
            left: clientX,
            top: clientY,
            right: clientX,
            bottom: clientY
          };
        }
      });
    },
    [refs]
  );

  useLayoutEffect(() => {
    if (followCursor) {
      window.addEventListener('mousemove', onMouseMove);
    }

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, [followCursor, onMouseMove]);

  return {
    refs,
    anchorRef: refs.reference,
    floatingRef: refs.floating,
    floatingStyles,
    update
  };
};
